///
/// EarthBrowser
///
/// Created by Matt Giger on 9/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Story card overlay shown during story playback: the current stop's
/// title/body, source links, progress dots and prev/next. When the user
/// grabs the globe mid-story (cameraMode "tourPaused") it collapses to a
/// single "Resume story" affordance so interruption feels intentional.
/// Ephemeral state only - driven by dialogSlice.activeStory, which
/// StoryPlayer keeps current.
///

import { sendCameraCommand } from "@/redux/earthBrowserSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { cn } from "@/utils/utils"
import { ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon, PlayIcon, XIcon } from "lucide-react"
import { useEffect } from "react"

export function StoryCard() {
	const dispatch = useAppDispatch()
	const activeStory = useAppSelector(state => state.dialog.activeStory)
	const cameraMode = useAppSelector(state => state.dialog.cameraMode)
	const story = useAppSelector(state => {
		if (!activeStory) return undefined
		const preview = state.dialog.sharedStoryPreview
		return state.stories.stories[activeStory.storyId]
			?? (preview?.id === activeStory.storyId ? preview : undefined)
	})

	const open = activeStory !== null && story !== undefined

	// Escape ends the story (matches "input always wins": the card is the
	// one piece of story UI, so Escape closes the whole experience)
	useEffect(() => {
		if (!open) return
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				dispatch(sendCameraCommand({ command: "story/stop" }))
			}
		}
		window.addEventListener("keydown", onKeyDown)
		return () => window.removeEventListener("keydown", onKeyDown)
	}, [open, dispatch])

	if (!open || !activeStory || !story) return null

	const stop = story.stops[activeStory.stopIndex]
	if (!stop) return null

	const paused = cameraMode === "tourPaused"
	const stopCount = story.stops.length
	const atFirst = activeStory.stopIndex === 0
	const atLast = activeStory.stopIndex === stopCount - 1

	// Paused: a single resume pill instead of the full card, so the viewer
	// can explore freely and fly back to the story when ready
	if (paused) {
		return (
			<div className={cn(
				"fixed bottom-28 left-1/2 -translate-x-1/2 z-40",
				"flex items-center gap-2",
				"animate-in fade-in slide-in-from-bottom-2 duration-300"
			)}>
				<button
					onClick={() => dispatch(sendCameraCommand({ command: "story/resume" }))}
					className={cn(
						"flex items-center gap-2 px-4 py-2 rounded-full",
						"bg-black/40 backdrop-blur-xl border border-white/20 shadow-2xl",
						"text-white text-sm font-medium",
						"hover:bg-black/60 transition-all duration-300 ease-out",
						"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
					)}
					aria-label="Resume story"
				>
					<PlayIcon className="w-4 h-4 text-cyan-300" />
					Resume story
				</button>
				<button
					onClick={() => dispatch(sendCameraCommand({ command: "story/stop" }))}
					className={cn(
						"p-2 rounded-full bg-black/40 backdrop-blur-xl border border-white/20 shadow-2xl",
						"text-white/60 hover:text-white hover:bg-black/60 transition-all duration-300"
					)}
					aria-label="End story"
				>
					<XIcon className="w-4 h-4" />
				</button>
			</div>
		)
	}

	return (
		<div className={cn(
			"fixed bottom-28 left-1/2 -translate-x-1/2 z-40",
			"w-[min(28rem,calc(100vw-2rem))]",
			"bg-black/40 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl",
			"animate-in fade-in slide-in-from-bottom-2 duration-300"
		)}>
			{/* Header: story title + close */}
			<div className="flex items-center justify-between px-4 pt-3">
				<span className="text-white/50 text-xs uppercase tracking-wider truncate">{story.title}</span>
				<button
					onClick={() => dispatch(sendCameraCommand({ command: "story/stop" }))}
					className="text-white/50 hover:text-white transition-colors shrink-0 ml-2"
					aria-label="End story"
				>
					<XIcon className="w-4 h-4" />
				</button>
			</div>

			{/* Narrative card */}
			<div className="px-4 pt-1 pb-2">
				{stop.title.length > 0 && (
					<h3 className="text-white text-base font-semibold">{stop.title}</h3>
				)}
				{stop.body.length > 0 && (
					<p className="text-white/80 text-sm mt-1 max-h-36 overflow-y-auto whitespace-pre-wrap">
						{stop.body}
					</p>
				)}
				{stop.sourceUrls && stop.sourceUrls.length > 0 && (
					<div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
						{stop.sourceUrls.map((url, index) => (
							<a
								key={url}
								href={url}
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-1 text-cyan-300/80 hover:text-cyan-200 text-xs transition-colors"
							>
								<ExternalLinkIcon className="w-3 h-3" />
								Source{stop.sourceUrls && stop.sourceUrls.length > 1 ? ` ${index + 1}` : ""}
							</a>
						))}
					</div>
				)}
			</div>

			{/* Footer: prev / progress dots / next */}
			<div className="flex items-center justify-between px-3 pb-3">
				<button
					onClick={() => dispatch(sendCameraCommand({ command: "story/prev" }))}
					disabled={atFirst}
					className={cn(
						"p-1.5 rounded-full transition-all duration-300",
						atFirst ? "text-white/20" : "text-white/70 hover:text-white hover:bg-white/10"
					)}
					aria-label="Previous stop"
				>
					<ChevronLeftIcon className="w-5 h-5" />
				</button>

				<div className="flex items-center gap-1.5" aria-label={`Stop ${activeStory.stopIndex + 1} of ${stopCount}`}>
					{story.stops.map((s, index) => (
						<span
							key={s.id}
							className={cn(
								"rounded-full transition-all duration-300",
								index === activeStory.stopIndex
									? "w-2 h-2 bg-gradient-to-r from-blue-400 to-cyan-400"
									: "w-1.5 h-1.5 bg-white/30"
							)}
						/>
					))}
				</div>

				<button
					onClick={() => dispatch(sendCameraCommand({ command: "story/next" }))}
					disabled={atLast}
					className={cn(
						"p-1.5 rounded-full transition-all duration-300",
						atLast ? "text-white/20" : "text-white/70 hover:text-white hover:bg-white/10"
					)}
					aria-label="Next stop"
				>
					<ChevronRightIcon className="w-5 h-5" />
				</button>
			</div>
		</div>
	)
}
